import {
  ApiError,
  getApiHeaders,
  handleUnauthorized,
  type EntityCustomFieldsView,
  type StandardApiResponse,
} from '@lognai/plugin-sdk';
import { customFieldsMinePath } from '@lognai/plugin-components/custom-fields';
import type {
  InvoiceDetail,
  InvoiceDraftInput,
  InvoicePerspective,
  Overview,
  Paginated,
  Payment,
  PaymentDetail,
  Settings,
  InvoiceRuleCatalogueResponse,
  InvoiceRulePack,
  CompanyInvoiceRule,
  CompanyRuleDraftInput,
  InvoiceIntakeDetail,
  SourceAllocation,
  SourceSuggestion,
  PaymentIntakeDetail,
  InvoiceWorkflow,
  InvoicePaymentPlan,
  InvoiceException,
  ApprovalQueueEntry,
} from './types';
import { notifyInvoiceError } from './lib/error-notifications';
import type { ConfirmInvoiceDocumentReview, InvoiceDocumentReview } from './lib/invoice-evidence-review';

const BASE = '/api/invoices';
const apiFetchRequest = async <T>(path: string, options: RequestInit = {}, base = BASE): Promise<T> => {
  const headers = new Headers(getApiHeaders());
  new Headers(options.headers).forEach((value, key) => headers.set(key, value));
  // FormData requests must not carry the JSON content type. The browser adds
  // the multipart boundary; forcing application/json makes Express try to
  // parse the boundary as JSON before multer can receive the files.
  if (typeof FormData !== 'undefined' && options.body instanceof FormData) {
    headers.delete('Content-Type');
  }
  const response = await fetch(`${base}${path}`, { cache: 'no-store', ...options, headers });
  if (response.status === 401) {
    handleUnauthorized();
    throw new ApiError('Unauthorized', { type: 'unauthorized' });
  }
  const payload = (await response
    .json()
    .catch(() => ({ success: false, error: response.statusText }))) as StandardApiResponse<T>;
  if (!response.ok || !payload.success)
    throw new ApiError(('error' in payload && payload.error) || `HTTP ${response.status}`, {
      // A refused custom field names its path, so the editor can mark it.
      ...((payload as { details?: { type?: string; field?: string } }).details ?? {}),
      status: response.status,
      value: 'details' in payload ? payload.details : undefined,
    });
  return payload.data;
};
const apiFetch = async <T>(path: string, options: RequestInit = {}, base = BASE): Promise<T> => {
  try { return await apiFetchRequest<T>(path, options, base); }
  catch (error) { notifyInvoiceError(error); throw error; }
};
const json = (method: string, body: unknown, mutation = true): RequestInit => ({
  method,
  headers: {
    'Content-Type': 'application/json',
    ...(mutation ? { 'Idempotency-Key': crypto.randomUUID() } : {}),
  },
  body: JSON.stringify(body),
});
const query = (params: Record<string, string | number | undefined>): string => {
  const value = new URLSearchParams();
  Object.entries(params).forEach(([key, item]) => {
    if (item !== undefined && item !== '') value.set(key, String(item));
  });
  return value.size ? `?${value}` : '';
};

/** Company custom fields on invoices. */
export const INVOICE_CUSTOM_FIELD_ENTITY = 'invoice';

const documentPreview = async (url: string, label = 'Document preview') => {
  try {
    const response = await fetch(url, { headers: getApiHeaders() });
    if (!response.ok) throw new ApiError(`${label} failed (${response.status})`);
    return URL.createObjectURL(await response.blob());
  } catch (error) { notifyInvoiceError(error); throw error; }
};

export const invoicesApi = {
  assignmentUsers: () => apiFetch<Array<{ value: string; label: string }>>('/assignment-users'),
  // Reuse the authorised Orders vendor directory, including active partner
  // vendors and tenant-owned legacy suppliers. A supplier id is not necessarily
  // a company id, so selecting a name never invents a connected-company link.
  vendors: async (search: string) => {
    const data = await apiFetch<{ items: Array<{ id: string; name: string; code?: string }> }>(
      `/suppliers${query({ q: search, limit: 20 })}`,
      {},
      '/api/orders'
    );
    return data.items.map((vendor) => ({
      id: vendor.id,
      name: vendor.name,
      sublabel: vendor.code,
    }));
  },
  overview: () => apiFetch<Overview>('/overview'),
  /** Not under `/api/invoices`: the admin plugin serves every entity's fields. */
  customFields: async (): Promise<EntityCustomFieldsView> => {
    const response = await fetch(customFieldsMinePath(INVOICE_CUSTOM_FIELD_ENTITY), {
      headers: getApiHeaders(),
    });
    const payload = (await response.json()) as StandardApiResponse<EntityCustomFieldsView>;
    if (!response.ok || !payload.success) throw new ApiError(`HTTP ${response.status}`);
    return payload.data;
  },
  list: (params: Record<string, string | number | undefined> = {}) =>
    apiFetch<Paginated<InvoicePerspective>>(`/${query(params)}`),
  detail: (id: string) => apiFetch<InvoiceDetail>(`/${encodeURIComponent(id)}`),
  documents: (id: string, kind: 'invoice' | 'payment' = 'invoice') => apiFetch<Array<import('./types').IntakeDocument & { citations: import('./types').EvidenceCitation[] }>>(`/${kind === 'payment' ? 'payments/' : ''}${encodeURIComponent(id)}/documents`),
  documentReviews: (id: string) => apiFetch<InvoiceDocumentReview[]>(`/${encodeURIComponent(id)}/document-reviews`),
  extractAttachment: (id: string, documentId: string, expectedVersion: number) => apiFetch<{ status: string }>(`/${encodeURIComponent(id)}/documents/${encodeURIComponent(documentId)}/extract`, json('POST', { expectedVersion })),
  confirmDocumentReview: (id: string, documentId: string, input: ConfirmInvoiceDocumentReview) => apiFetch<InvoicePerspective>(`/${encodeURIComponent(id)}/documents/${encodeURIComponent(documentId)}/review`, json('POST', input)),
  previewDocument: async (id: string, documentId: string, kind: 'invoice' | 'payment' = 'invoice') => {
    return documentPreview(`${BASE}/${kind === 'payment' ? 'payments/' : ''}${encodeURIComponent(id)}/documents/${encodeURIComponent(documentId)}`);
  },
  attachEvidence: (id: string, files: File[], expectedVersion: number) => {
    const body = new FormData(); body.append('expectedVersion', String(expectedVersion)); files.forEach((file) => body.append('files', file));
    return apiFetch<InvoicePerspective>(`/${encodeURIComponent(id)}/documents`, { method: 'POST', headers: { 'Idempotency-Key': crypto.randomUUID() }, body });
  },
  requestNonPo: (id: string, body: { expectedVersion: number; reason: string; evidenceDocumentIds: string[] }) => apiFetch<Record<string, unknown>>(`/${encodeURIComponent(id)}/non-po-authorisation`, json('POST', body)),
  respondEvidence: (id: string, body: { expectedVersion: number; reason: string; evidenceDocumentIds: string[] }) => apiFetch<{ requestId: string; status: string }>(`/findings/${encodeURIComponent(id)}/evidence-response`, json('POST', body)),
  create: (input: InvoiceDraftInput) => apiFetch<InvoicePerspective>('/', json('POST', input)),
  startIntake: (files: File[], direction: 'payable' | 'receivable' = 'payable') => {
    const body = new FormData();
    body.append('direction', direction);
    files.forEach((file) => body.append('files', file));
    return apiFetch<InvoiceIntakeDetail>('/intake/sessions', {
      method: 'POST',
      headers: { 'Idempotency-Key': crypto.randomUUID() },
      body,
    });
  },
  intake: (sessionId: string) =>
    apiFetch<InvoiceIntakeDetail>(`/intake/sessions/${encodeURIComponent(sessionId)}`),
  resumableIntakes: () => apiFetch<Array<{ sessionId: string; kind: string; status: string; updatedAt: string; route: string }>>('/intake/resumable'),
  retryIntakeRun: (sessionId: string, runId: string, expectedVersion: number) =>
    apiFetch<InvoiceIntakeDetail['runs'][number]>(
      `/intake/sessions/${encodeURIComponent(sessionId)}/runs/${encodeURIComponent(runId)}/retry`,
      json('POST', { expectedVersion })
    ),
  saveIntakeReview: (
    sessionId: string,
    body: {
      expectedVersion: number;
      step?: 'review' | 'connections' | 'confirm';
      groupId?: string;
      candidateVersionId?: string;
      reviewedCandidate: Record<string, unknown>;
      sourceAllocations?: SourceAllocation[];
      nonPoReason?: string;
    }
  ) =>
    apiFetch<InvoiceIntakeDetail['reviews'][number]>(
      `/intake/sessions/${encodeURIComponent(sessionId)}/review`,
      json('PUT', body)
    ),
  saveIntakeGroups: (
    sessionId: string,
    body: {
      expectedVersion: number;
      groups: Array<{ id?: string; documentIds: string[]; primaryDocumentId: string }>;
    }
  ) =>
    apiFetch<InvoiceIntakeDetail['documentGroups'][number]>(
      `/intake/sessions/${encodeURIComponent(sessionId)}/document-groups`,
      json('PUT', body)
    ),
  intakeSourceSuggestions: (sessionId: string, groupId?: string, options: { search?: string; sourceType?: string; browse?: boolean } = {}) =>
    apiFetch<SourceSuggestion[]>(
      `/intake/sessions/${encodeURIComponent(sessionId)}/source-suggestions${query({ groupId, search: options.search || undefined, sourceType: options.sourceType || undefined, browse: options.browse ? 'true' : undefined })}`
    ),
  cancelIntake: (sessionId: string, expectedVersion: number) =>
    apiFetch<InvoiceIntakeDetail['session']>(
      `/intake/sessions/${encodeURIComponent(sessionId)}/cancel`,
      json('POST', { expectedVersion })
    ),
  confirmIntake: (sessionId: string, input: InvoiceDraftInput, expectedIntakeVersion?: number) =>
    apiFetch<{ sessionId: string; sessionVersion: number; invoices: InvoicePerspective[] }>(
      `/intake/sessions/${encodeURIComponent(sessionId)}/confirm`,
      json('POST', { ...input, expectedIntakeVersion })
    ),
  previewIntakeDocument: async (sessionId: string, documentId: string) => {
    return documentPreview(`${BASE}/intake/sessions/${encodeURIComponent(sessionId)}/documents/${encodeURIComponent(documentId)}`);
  },
  startPortalIntake: (ownerCompanyId: string, files: File[]) => {
    const body = new FormData();
    files.forEach((file) => body.append('files', file));
    return apiFetch<InvoiceIntakeDetail>(
      `/portal/${encodeURIComponent(ownerCompanyId)}/intake/sessions`,
      { method: 'POST', headers: { 'Idempotency-Key': crypto.randomUUID() }, body }
    );
  },
  portalIntake: (sessionId: string) =>
    apiFetch<InvoiceIntakeDetail>(`/portal/intake/sessions/${encodeURIComponent(sessionId)}`),
  previewPortalIntakeDocument: async (sessionId: string, documentId: string) => {
    return documentPreview(`${BASE}/portal/intake/sessions/${encodeURIComponent(sessionId)}/documents/${encodeURIComponent(documentId)}`);
  },
  retryPortalIntakeRun: (sessionId: string, runId: string, expectedVersion: number) =>
    apiFetch<InvoiceIntakeDetail['runs'][number]>(
      `/portal/intake/sessions/${encodeURIComponent(sessionId)}/runs/${encodeURIComponent(runId)}/retry`,
      json('POST', { expectedVersion })
    ),
  savePortalIntakeReview: (
    sessionId: string,
    body: {
      expectedVersion: number;
      step?: 'review' | 'connections' | 'confirm';
      groupId?: string;
      candidateVersionId?: string;
      reviewedCandidate: Record<string, unknown>;
    }
  ) =>
    apiFetch<InvoiceIntakeDetail['reviews'][number]>(
      `/portal/intake/sessions/${encodeURIComponent(sessionId)}/review`,
      json('PUT', body)
    ),
  savePortalIntakeGroups: (sessionId: string, body: { expectedVersion: number; groups: Array<{ id?: string; documentIds: string[]; primaryDocumentId: string }> }) =>
    apiFetch<InvoiceIntakeDetail['documentGroups'][number]>(`/portal/intake/sessions/${encodeURIComponent(sessionId)}/document-groups`, json('POST', body)),
  confirmPortalIntake: (
    sessionId: string,
    input: InvoiceDraftInput,
    expectedIntakeVersion: number
  ) =>
    apiFetch<{ sessionId: string; sessionVersion: number; invoices: InvoicePerspective[] }>(
      `/portal/intake/sessions/${encodeURIComponent(sessionId)}/confirm`,
      json('POST', { ...input, expectedIntakeVersion })
    ),
  cancelPortalIntake: (sessionId: string, expectedVersion: number) =>
    apiFetch<InvoiceIntakeDetail['session']>(
      `/portal/intake/sessions/${encodeURIComponent(sessionId)}/cancel`,
      json('POST', { expectedVersion })
    ),
  update: (id: string, input: Record<string, unknown>) =>
    apiFetch<InvoicePerspective>(`/${encodeURIComponent(id)}`, json('PATCH', input)),
  correctTranscription: (id: string, input: InvoiceDraftInput & { expectedVersion: number; reason: string; evidenceDocumentIds: string[] }) => apiFetch<InvoicePerspective>(`/${encodeURIComponent(id)}/transcription-corrections`, json('POST', input)),
  command: (id: string, command: string, body: Record<string, unknown>) =>
    apiFetch<unknown>(`/${encodeURIComponent(id)}/${command}`, json('POST', body)),
  exceptions: (params: Record<string, string | number | undefined> = {}) =>
    apiFetch<InvoiceException[]>(`/exceptions${query(params)}`),
  resolveFinding: (
    findingId: string,
    body: {
      decision: 'resolved' | 'accepted' | 'request_evidence';
      reason: string;
      evidenceDocumentIds: string[];
      expectedVersion: number;
      expectedAuditRunId: string;
    }
  ) =>
    apiFetch<Record<string, unknown>>(
      `/findings/${encodeURIComponent(findingId)}/resolve`,
      json('POST', body)
    ),
  sourceSuggestions: (id: string, search?: string, sourceType?: string) =>
    apiFetch<Array<Record<string, unknown>>>(
      `/${encodeURIComponent(id)}/source-suggestions${query({ search, sourceType })}`
    ),
  connectedOrder: (kind: 'purchase_order' | 'sales_order', id: string) =>
    apiFetch<{
      id: string;
      orderNumber?: string;
      poNumber?: string;
      soNumber?: string;
      status?: string;
    }>(
      `/${kind === 'purchase_order' ? 'purchase-orders' : 'sales-orders'}/${encodeURIComponent(id)}`,
      {},
      '/api/orders'
    ),
  workflow: (id: string) => apiFetch<InvoiceWorkflow>(`/${encodeURIComponent(id)}/workflow`),
  paymentPlan: (id: string) =>
    apiFetch<InvoicePaymentPlan | null>(`/${encodeURIComponent(id)}/payment-plan`),
  createPaymentPlan: (id: string, body: Record<string, unknown>) =>
    apiFetch<InvoicePaymentPlan>(`/${encodeURIComponent(id)}/payment-plan`, json('POST', body)),
  paymentPlans: (id: string) =>
    apiFetch<InvoicePaymentPlan[]>(`/${encodeURIComponent(id)}/payment-plans`),
  previewPaymentPlan: (id: string, body: Record<string, unknown>) =>
    apiFetch<Record<string, unknown>>(
      `/${encodeURIComponent(id)}/payment-plan/preview`,
      json('POST', body, false)
    ),
  cancelPaymentPlan: (id: string, planId: string, reason: string) =>
    apiFetch<InvoicePaymentPlan>(
      `/${encodeURIComponent(id)}/payment-plans/${encodeURIComponent(planId)}/cancel`,
      json('POST', { reason })
    ),
  settings: () => apiFetch<Settings>('/settings'),
  intakeDefaults: () => apiFetch<Pick<Settings, 'legalName' | 'countryCode' | 'countryPack' | 'taxId' | 'address' | 'baseCurrency' | 'timezone' | 'defaultPaymentTermsDays' | 'version' | 'connectionRequirements'>>('/intake/defaults'),
  saveSettings: (settings: Settings) =>
    apiFetch<Settings>(
      '/settings',
      json('PUT', { ...settings, expectedVersion: settings.version })
    ),
  policies: () => apiFetch<import('./types').ApprovalPolicy[]>('/approval-policies'),
  approvalRoles: () => apiFetch<Array<{ value: string; label: string }>>('/approval-policies/roles'),
  approvalUsers: () => apiFetch<Array<{ value: string; label: string; email: string }>>('/approval-policies/users'),
  createApprovalPolicy: (
    body: Omit<import('./types').ApprovalPolicy, 'id' | 'version' | 'status' | 'createdAt'>
  ) => apiFetch<import('./types').ApprovalPolicy>('/approval-policies', json('POST', body)),
  previewApproval: (params: Record<string, string | undefined>) =>
    apiFetch<{
      required: boolean;
      policy: import('./types').ApprovalPolicy | null;
      stages: import('./types').ApprovalPolicy['stages'];
    }>(`/approval-preview${query(params)}`),
  auditPolicies: () => apiFetch<Array<Record<string, unknown>>>('/audit-policies'),
  activatePolicy: (kind: 'approval' | 'audit', id: string) =>
    apiFetch<Record<string, unknown>>(
      `/${kind}-policies/${encodeURIComponent(id)}/activate`,
      json('POST', {})
    ),
  auditRuleCatalogue: () => apiFetch<InvoiceRuleCatalogueResponse>('/audit-rules/catalogue'),
  activeAuditRulePack: () => apiFetch<InvoiceRulePack>('/audit-rule-packs/active'),
  previewAuditRulePack: (body: Record<string, unknown>) =>
    apiFetch<Record<string, unknown>>('/audit-rule-packs/preview', json('POST', body, false)),
  createAuditRulePack: (body: Record<string, unknown>) =>
    apiFetch<InvoiceRulePack>('/audit-rule-packs', json('POST', body)),
  activateAuditRulePack: (id: string) =>
    apiFetch<InvoiceRulePack>(
      `/audit-rule-packs/${encodeURIComponent(id)}/activate`,
      json('POST', {})
    ),
  validateCustomAuditRule: (expression: Record<string, unknown>) =>
    apiFetch<{ valid: boolean }>(
      '/audit-rules/custom/validate',
      json('POST', { expression }, false)
    ),
  createCustomAuditRule: (body: CompanyRuleDraftInput) =>
    apiFetch<CompanyInvoiceRule>('/audit-rules/custom', json('POST', body)),
  draftAuditRuleWithAi: (prompt: string) =>
    apiFetch<CompanyRuleDraftInput>('/audit-rules/ai/draft', json('POST', { prompt }, false)),
  dryRunAuditRule: (id: string) =>
    apiFetch<{ sampled: number; pass: number; fail: number; indeterminate: number }>(
      `/audit-rules/${encodeURIComponent(id)}/dry-run`,
      json('POST', {}, false)
    ),
  activateAuditRule: (id: string) =>
    apiFetch<CompanyInvoiceRule>(
      `/audit-rules/${encodeURIComponent(id)}/activate`,
      json('POST', {})
    ),
  approvals: (status?: string, subjectId?: string) =>
    apiFetch<ApprovalQueueEntry[]>(`/approvals${query({ status, subjectId })}`),
  decideApproval: (id: string, body: Record<string, unknown>) =>
    apiFetch<Record<string, unknown>>(
      `/approvals/${encodeURIComponent(id)}/decision`,
      json('POST', body)
    ),
  portal: () => apiFetch<InvoicePerspective[]>('/portal'),
  portalOwners: () => apiFetch<Array<{ companyId: string; name: string }>>('/portal/owners'),
  submitPortalInvoice: (ownerCompanyId: string, input: InvoiceDraftInput) =>
    apiFetch<InvoicePerspective>(
      `/portal/owners/${encodeURIComponent(ownerCompanyId)}/submissions`,
      json('POST', input)
    ),
  portalDetail: (exchangeId: string) =>
    apiFetch<InvoiceDetail>(`/portal/${encodeURIComponent(exchangeId)}`),
  payments: (params: Record<string, string | number | undefined> = {}) =>
    apiFetch<Paginated<Payment>>(`/payments${query(params)}`),
  remittanceCandidates: () =>
    apiFetch<
      Array<{
        id: string;
        senderCompanyId: string;
        amount: string;
        currency: string;
        externalReference: string;
        status: string;
        version: number;
        createdAt: string;
      }>
    >('/payments/remittance-candidates'),
  decideRemittance: (id: string, body: Record<string, unknown>) =>
    apiFetch<Record<string, unknown>>(
      `/payments/remittance-candidates/${encodeURIComponent(id)}/decision`,
      json('POST', body)
    ),
  payment: (id: string) => apiFetch<PaymentDetail>(`/payments/${encodeURIComponent(id)}`),
  paymentSuggestions: (id: string) =>
    apiFetch<Array<{ invoice: InvoicePerspective; score: number; reasons: string[] }>>(
      `/payments/${encodeURIComponent(id)}/suggestions`
    ),
  previewPaymentAllocations: (id: string, assignments: Array<Record<string, unknown>>) =>
    apiFetch<Record<string, unknown>>(
      `/payments/${encodeURIComponent(id)}/allocations/preview`,
      json('POST', { assignments }, false)
    ),
  allocatePaymentBatch: (id: string, body: Record<string, unknown>) =>
    apiFetch<Record<string, unknown>>(
      `/payments/${encodeURIComponent(id)}/allocations/batch`,
      json('POST', body)
    ),
  createPayment: (input: Record<string, unknown>) =>
    apiFetch<Payment>('/payments', json('POST', input)),
  startPaymentIntake: (files: File[]) => {
    const body = new FormData();
    files.forEach((file) => body.append('files', file));
    return apiFetch<PaymentIntakeDetail>('/payments/intake/sessions', {
      method: 'POST',
      headers: { 'Idempotency-Key': crypto.randomUUID() },
      body,
    });
  },
  paymentIntake: (sessionId: string) =>
    apiFetch<PaymentIntakeDetail>(`/payments/intake/sessions/${encodeURIComponent(sessionId)}`),
  previewPaymentIntakeDocument: async (sessionId: string, documentId: string) => {
    return documentPreview(`${BASE}/payments/intake/sessions/${encodeURIComponent(sessionId)}/documents/${encodeURIComponent(documentId)}`, 'Payment evidence preview');
  },
  retryPaymentIntake: (sessionId: string, runId: string, expectedVersion: number) =>
    apiFetch<PaymentIntakeDetail['runs'][number]>(
      `/payments/intake/sessions/${encodeURIComponent(sessionId)}/runs/${encodeURIComponent(runId)}/retry`,
      json('POST', { expectedVersion })
    ),
  cancelPaymentIntake: (sessionId: string, expectedVersion: number) =>
    apiFetch<PaymentIntakeDetail['session']>(
      `/payments/intake/sessions/${encodeURIComponent(sessionId)}/cancel`,
      json('POST', { expectedVersion })
    ),
  savePaymentIntakeReview: (sessionId: string, expectedVersion: number, review: NonNullable<PaymentIntakeDetail['session']['review']>) => apiFetch<PaymentIntakeDetail['session']>(`/payments/intake/sessions/${encodeURIComponent(sessionId)}/review`, json('PUT', { expectedVersion, review })),
  paymentCommand: (id: string, command: string, body: Record<string, unknown>) =>
    apiFetch<unknown>(`/payments/${encodeURIComponent(id)}/${command}`, json('POST', body)),
};
